import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Trophy, Medal, Award, TrendingUp, TrendingDown, ChevronLeft, ChevronRight, Download } from 'lucide-react';
import api from '../lib/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const MEDALS = {
  1: { icon: Trophy, color: 'text-amber-500', bg: 'bg-amber-50', ring: 'ring-amber-300' },
  2: { icon: Medal, color: 'text-slate-500', bg: 'bg-slate-50', ring: 'ring-slate-300' },
  3: { icon: Award, color: 'text-orange-600', bg: 'bg-orange-50', ring: 'ring-orange-300' },
};

const monthLabel = (yyyymm) => {
  if (!yyyymm) return '';
  const [y, m] = yyyymm.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
};

const shiftMonth = (yyyymm, delta) => {
  const [y, m] = yyyymm.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

export const RMLeaderboard = () => {
  const currentMonth = useMemo(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }, []);
  const [month, setMonth] = useState(currentMonth);
  const [data, setData] = useState({ month, rows: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.rmLeaderboard(month)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setData({ month, rows: [] }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [month]);

  const rows = data.rows || [];
  const topScore = rows[0]?.score || 0;
  const isFuture = month >= currentMonth;

  return (
    <Card className="border-brand-border" data-testid="rm-leaderboard">
      <CardContent className="p-0">
        <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-brand-indigo flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500" /> Monthly RM Leaderboard
            </div>
            <div className="text-[11px] text-muted-foreground">
              Ranked by weighted score: <span className="font-semibold">10× completed MRIs</span> + <span className="font-semibold">6× signed reports</span> + <span className="font-semibold">2× on-time follow-ups</span> − <span className="font-semibold">4× overdue</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <a
              href={`${API_BASE}/api/admin/rm-leaderboard.csv?month=${month}`}
              className="mr-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-brand-border text-[12px] font-semibold text-brand-indigo hover:bg-brand-bg"
              data-testid="lb-export-csv"
              download
              target="_blank"
              rel="noreferrer"
            >
              <Download className="h-3.5 w-3.5" /> CSV
            </a>
            <button
              type="button"
              data-testid="lb-month-prev"
              onClick={() => setMonth((m) => shiftMonth(m, -1))}
              className="h-8 w-8 rounded-md border border-brand-border flex items-center justify-center hover:bg-brand-bg"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-4 w-4 text-brand-indigo" />
            </button>
            <div className="px-3 py-1.5 text-sm font-semibold text-brand-indigo border border-brand-border rounded-md min-w-[140px] text-center" data-testid="lb-month-label">
              {monthLabel(month)}
            </div>
            <button
              type="button"
              data-testid="lb-month-next"
              onClick={() => setMonth((m) => shiftMonth(m, 1))}
              disabled={isFuture}
              className="h-8 w-8 rounded-md border border-brand-border flex items-center justify-center hover:bg-brand-bg disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Next month"
            >
              <ChevronRight className="h-4 w-4 text-brand-indigo" />
            </button>
          </div>
        </div>

        {loading && <div className="p-6 text-sm text-muted-foreground">Loading leaderboard…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-sm text-muted-foreground">No activity recorded for {monthLabel(month)}.</div>
        )}
        {!loading && rows.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-brand-border">
                  <th className="px-5 py-3 font-semibold w-16">Rank</th>
                  <th className="px-5 py-3 font-semibold">Relationship Manager</th>
                  <th className="px-5 py-3 font-semibold text-right">Completed MRIs</th>
                  <th className="px-5 py-3 font-semibold text-right">Signed Reports</th>
                  <th className="px-5 py-3 font-semibold text-right">Follow-ups</th>
                  <th className="px-5 py-3 font-semibold text-right">Score</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const medal = MEDALS[r.rank];
                  const Icon = medal?.icon;
                  const trending = r.score >= topScore * 0.7 ? 'up' : r.score <= 0 ? 'down' : null;
                  return (
                    <tr key={r.rm_id} data-testid={`lb-row-${r.rm_id}`} className={`border-b border-brand-border/50 hover:bg-brand-bg ${r.rank === 1 ? 'bg-amber-50/40' : ''}`}>
                      <td className="px-5 py-3">
                        {medal ? (
                          <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${medal.bg} ring-2 ${medal.ring}`}>
                            <Icon className={`h-4 w-4 ${medal.color}`} />
                          </span>
                        ) : (
                          <span className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-brand-bg text-brand-indigo font-bold text-sm">
                            {r.rank}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-semibold text-brand-indigo">{r.rm_name}</div>
                        <div className="text-[11px] text-muted-foreground">EUIN {r.euin || '—'}</div>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-brand-indigo">{r.completed_mri}</td>
                      <td className="px-5 py-3 text-right font-semibold text-brand-indigo">{r.signed_reports}</td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-emerald-600 font-semibold">{r.followups_on_time}</span>
                        {r.followups_overdue > 0 && <span className="text-brand-red font-semibold ml-2">· {r.followups_overdue} overdue</span>}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {trending === 'up' && <TrendingUp className="h-3.5 w-3.5 text-emerald-600" />}
                          {trending === 'down' && <TrendingDown className="h-3.5 w-3.5 text-brand-red" />}
                          <span className={`text-base font-bold ${r.score >= 0 ? 'text-brand-indigo' : 'text-brand-red'}`}>{r.score}</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RMLeaderboard;
